import * as React from 'react'
import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import BackgroundImage from 'components/common/background-image'
import UINotificationSystem from 'components/common/ui-notification-system'
import CookieBanner from 'components/modals/cookie-banner'
import PublicPage from 'components/public/page'
import FileDropWrapper from 'containers/FileDropWrapper'
import NotificationManager from '@disco/universal-components/NotificationManager'
import FileDropOverlay from 'pages/upload/file-drop-overlay'
import FileReceiver from 'pages/upload/file-receiver'
import PlayerContainer from 'pages/upload/player-container'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { BusinessData } from 'types/business'
import type { DiscoPlaylist, InboxGroup, InboxPlaylist } from 'types/inbox'
import type { Business } from 'types/public-playlist'
import type { User } from 'types/user'
import { pushUINotification } from '@disco/utils/notifications'
import { prepareTempItems, taskRunner, verifyFiles } from '@disco/utils/upload'
import {
  insertUploadTempItems,
  removeUploadTempItem,
  sendDiscoPlaylist,
  sendPlaylist,
  setDiscoPlaylist,
  uploadFileToDisco,
} from '@disco/state-management/actions/UploadActions'
import { deleteTrack } from '@disco/state-management/actions/ServerActions'
import UploadFooter from 'views/pages/upload/upload-footer'
import UploadFooterInbox from 'views/pages/upload/upload-footer-inbox'
import type { Location } from 'react-router-dom'
import { BriefNotFound } from 'features/briefs/pages/not-found'

const submitButtonCaption = {
  caption: 'Submit Response',
  progress: 'Submitting Response',
}

export const SubmissionForm = ({
  group,
  location,
  children,
  businessData,
  preview,
  params,
}: {
  group: InboxGroup
  children?: React.ReactNode
  businessData: BusinessData
  customMessage?: string | null | undefined
  location: Location
  preview?: boolean
  params: {
    groupId: string
    groupName: string
  }
}) => {
  const app = useSelector(state => state.app)
  const playlist = useSelector(state => state.upload)
  const { discoPlaylist, discoUser, businessList } = playlist

  const uploadFileToDiscoAction = (
    ...args: Parameters<typeof uploadFileToDisco>
  ) => dispatch(uploadFileToDisco(...args))
  const sendPlaylistAction = (...args: Parameters<typeof sendPlaylist>) =>
    dispatch(sendPlaylist(...args))
  const sendDiscoPlaylistAction = (
    ...args: Parameters<typeof sendDiscoPlaylist>
  ) => dispatch(sendDiscoPlaylist(...args))
  const setDiscoPlaylistAction = (
    ...args: Parameters<typeof setDiscoPlaylist>
  ) => dispatch(setDiscoPlaylist(...args))
  const deleteTrackAction = (...args: Parameters<typeof deleteTrack>) =>
    dispatch(deleteTrack(...args))

  const dispatch = useDispatch()

  const uploadFiles = (files: File[]) => {
    // Prepare temp items and verify files
    const tempItems = verifyFiles(prepareTempItems(files), pushUINotification)
    dispatch(insertUploadTempItems(tempItems))
    return api
      .getCSRFToken()
      .then(csrfToken => {
        tempItems.forEach(tempItem => {
          taskRunner.addTask({
            id: tempItem.tempId,
            func: uploadFileToDiscoAction,
            params: [csrfToken, tempItem],
          })
        })
      })
      .catch(error => console.log(error))
  }
  const onFileSelect = (files: File[]) => {
    mixpanel.sendPureEvent('Upload files via file selection dialog (Inbox)')
    uploadFiles(files)
  }

  const onFileDrop = (files: File[]) => {
    mixpanel.sendPureEvent('Upload files via drag and drop (Inbox)')
    uploadFiles(files)
  }

  const onUploadCancel = item => {
    taskRunner.removeTask(item.tempId)
    dispatch(removeUploadTempItem(item))
  }

  if (group.brief_data.submission_status === 'closed') {
    return <BriefNotFound />
  }

  const form = (
    <PublicPage fullHeight={playlist.sent} backgroundWhite={playlist.sent}>
      <CookieBanner location="Upload" />
      {!playlist.sent && <BackgroundImage />}
      <FileReceiver
        formCaption={`${group.brief_data.name}`}
        inbox={group}
        onFileSelect={onFileSelect}
        onUploadCancel={onUploadCancel}
        params={{ ...params, path: 'brief' }}
        app={app}
        playlist={playlist as any}
        businessData={businessData}
        businessList={businessList}
        discoPlaylist={discoPlaylist}
        discoUser={discoUser}
        location={location}
        deleteTrack={deleteTrackAction}
        sendPlaylist={sendPlaylistAction}
        sendDiscoPlaylist={sendDiscoPlaylistAction}
        setDiscoPlaylist={setDiscoPlaylistAction}
        submitButtonCaption={submitButtonCaption}
        preview={preview}
      />
      {playlist.sent ? (
        <UploadFooterInbox
          displayLogo={businessData.public_metadata?.show_disco_logo}
          businessName={businessData.name}
          isBrief
        />
      ) : (
        <UploadFooter />
      )}
      <PlayerContainer />
      <NotificationManager location={location} />
      <UINotificationSystem />
    </PublicPage>
  )

  if (preview) {
    return (
      <>
        {form}
        {children}
      </>
    )
  }

  return (
    <FileDropWrapper
      overlay={<FileDropOverlay targetText={group.brief_data.name} />}
      onDrop={onFileDrop}
      disabled={!!discoPlaylist}
      multiple
    >
      {form}
      {children}
    </FileDropWrapper>
  )
}
